import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';

// Main container for TaskDetails
export const MainContainer = styled(Box)(({ theme }) => ({
  padding: 0,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

// Header container 
export const HeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

// Back button with specific styling
export const BackButton = styled(Button)({
  minWidth: 'auto',
  padding: 0,
});

// Base task progress container - can be extended for different use cases
export const BaseTaskProgressContainer = styled(Box)({
  display: 'flex',
  position: 'relative',
});

// Task progress sidebar container - extends base for sidebar layout
export const TaskProgressSidebarContainer = styled(BaseTaskProgressContainer)(({ theme }) => ({
  minWidth: '350px',
  padding: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`,
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
}));

// Task progress container for table columns - extends base for table layout
export const TaskProgressContainer = styled(BaseTaskProgressContainer)({
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  paddingLeft: '16px', 
});

// Task progress title
export const TaskProgressTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(3),
  color: theme.palette.text.primary,
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  flexShrink: 0,
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

// Task list container with custom scrollbar
export const TaskListContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'auto',
  paddingLeft: 8,
  paddingRight: 8,
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '2px',
    '&:hover': {
      background: '#a8a8a8',
    },
  },
});

// Individual task item container
export const TaskItemWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  minHeight: '50px',
  marginBottom: 8,
});

// Task connector line
export const TaskConnectorLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '12.5px',
  top: '25px',
  bottom: '-25px',
  width: '2px',
  backgroundColor: theme.palette.divider,
  zIndex: 0,
}));

// Task item content container
export const TaskItemContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  paddingTop: 4,
  paddingBottom: 4,
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  paddingLeft: 4,
  paddingRight: 4,
  transition: 'all 0.2s ease',
  position: 'relative',
  zIndex: 1,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Status stepper container
export const StatusStepperContainer = styled(Box)({
  width: '22px',
  height: '22px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

// Task title with dynamic styling
export const TaskTitle = styled(Typography)(({ theme, status, isSelected }) => ({
  color: status === 'completed' ? theme.palette.success.main :
         (status === 'inprogress' || status === 'in_progress') ? theme.palette.warning.main :
         status === 'pending' ? theme.palette.success.main :
         theme.palette.text.secondary,
  fontWeight: isSelected ? 700 : 500,
  transition: 'all 0.2s ease',
  flex: 1,
  minWidth: 0,
  fontSize: '0.875rem',
}));

// Connector line to the right - for left-aligned steppers
export const ConnectorLineRight = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '27px', // Start from stepper position (16px padding + 11px center of 22px stepper)
  right: 0,
  height: '2px',
  backgroundColor: theme.palette.divider,
  transform: 'translateY(-50%)',
  zIndex: 0,
}));

// Connector line to the left - for left-aligned steppers  
export const ConnectorLineLeft = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 'calc(100% - 27px)', // End at stepper position
  height: '2px',
  backgroundColor: theme.palette.divider,
  transform: 'translateY(-50%)',
  zIndex: 0,
}));

// Progress line to the left (green for completed steps)
export const ProgressLineLeft = styled(Box)(({ isCompleted }) => ({
  position: 'absolute',
  top: '50%',
  left: 0,
  right: '50%',
  height: '2px',
  backgroundColor: isCompleted ? '#4caf50' : 'transparent',
  transform: 'translateY(-50%)',
  zIndex: 1,
}));

// Progress line to the right (green for completed current task)
export const ProgressLineRight = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  right: 0,
  height: '2px',
  backgroundColor: '#4caf50',
  transform: 'translateY(-50%)',
  zIndex: 1,
});

// Status stepper wrapper for table
export const StatusStepperWrapper = styled(Box)({
  position: 'relative',
  zIndex: 2,
  flexShrink: 0, // Prevent shrinking
});

// Status chip container
export const StatusChipContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

// Main task details container
export const TaskDetailsMainContainer = styled(Box)(({ theme }) => ({
  padding: 0,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

// Content wrapper for task details
export const TaskDetailsContentWrapper = styled(Box)({
  display: 'flex',
  height: 'calc(100vh - 250px)',
});

// Main content area with custom scrollbar
export const TaskDetailsMainContent = styled(Box)({
  flex: 1,
  padding: 16,
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
    '&:hover': {
      background: '#a8a8a8',
    },
  },
});

// Footer container for task details
export const TaskDetailsFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  marginTop: 'auto',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
}));



// Content container - layout specific
export const ContentContainer = styled(Box)({
  display: 'flex',
  height: 'calc(100vh - 300px)',
});

// Main content area - complex layout
export const MainContentArea = styled(Box)(({ theme }) => ({
  width: '80%',
  overflow: 'auto',
}));

// Footer container - complex layout
export const FooterContainer = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  marginTop: 'auto',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
}));

// Task item container
export const TaskItemContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  minHeight: '60px',
});

// Content section container
export const ContentSectionContainer = styled(Box)({
  marginBottom: 24,
});

// Content section content
export const ContentSectionContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
}));